import _ from 'lodash';
import { useMsal, } from '@azure/msal-react';
import { useParams } from "react-router-dom";
import TenantApi from '../../api/tenant/tenant-api-factory';
import XeroApi from '../../api/xero/xero-api-factory';
import Comparer from "../../app/comparer";
import { useState, useEffect, useMemo, createContext } from "react";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { useLocation, Outlet } from 'react-router-dom';
import { TenantDetailsTab } from '../../components/tenants/tenant-detail-tab';
import { PaymentsTab } from '../../components/tenants/payments-tab';
import { TenanciesTab } from '../../components/tenants/tenancies-tab';
import { TenantsDocumentsTab } from '../../components/tenants/tenant-documents-tab';

export const TenantContext = createContext();

export const TenantDetail = () => {

    const comparer = new Comparer();

    const { id, tab } = useParams();
    const { instance, inProgress, accounts } = useMsal();

    const tenantApi = useMemo(() => new TenantApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const xeroApi = useMemo(() => new XeroApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const [state, setState] = useState(null);
    const [originalState, setOriginalState] = useState(null);
    const [fetchTenantInProgress, setFetchTenantInProgress] = useState(false);
    const [shouldFetchTenant, setShouldFetchTenant] = useState(true);
    const [validationErrors, setValidationErrors] = useState(null);

    const [bankData, setBankData] = useState(null);
    const [fetchBankInProgress, setFetchBankInProgress] = useState(false);
    const [shouldFetchBank, setShouldFetchBank] = useState(true);
    const [bankDataError, setBankDataError] = useState(null);

    const location = useLocation();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        async function fetchData() {
            if (shouldFetchTenant && !fetchTenantInProgress) {
                setFetchTenantInProgress(true);
                try {
                    const data = await tenantApi.get(id);
                    setState(data);
                    setOriginalState(structuredClone(data));
                } catch (error) {
                    console.log("An error occurred getting data from the Tenant Data API.", error);
                    setState(null);
                }
                finally {
                    setShouldFetchTenant(false);
                    setFetchTenantInProgress(false);
                }
            }
        }
        fetchData();
    }, [shouldFetchTenant, fetchTenantInProgress, id, tenantApi]);

    useEffect(() => {
        async function fetchData() {
            if (state && state.xeroContact && shouldFetchBank && !fetchBankInProgress) {
                setFetchBankInProgress(true);
                setBankDataError(null); // Reset error state before fetching
                try {
                    const data = await xeroApi.getBankTransactionsForContact(state.companyId, state.xeroContact.xeroContactId);
                    setBankData(data);
                } catch (error) {
                    console.log("An error occurred getting data from the Xero API.", error);
                    setBankData(null);
                    
                    // Check for authorization errors (typically 401 or 403)
                    if (error.status === 401 || error.status === 403) {
                        setBankDataError("You don't have permission to access bank transaction data");
                    } else {
                        setBankDataError("Failed to fetch bank transaction data");
                        console.error("Error fetching bank transactions:", error);
                    }
                } finally {
                    setShouldFetchBank(false);
                    setFetchBankInProgress(false);
                }
            }
        }
        fetchData();
    }, [state, shouldFetchBank, fetchBankInProgress, xeroApi]);

    useEffect(() => {
        setState(structuredClone(originalState));
    }, [originalState]);

    const hasChanges = () => {
        const equal = comparer.equals(state, originalState);
        return !equal;
    };

    const hasValidationErrors = () => {
        return validationErrors && Object.keys(validationErrors).length > 0;
    };

    const canSave = () => {
        let result = hasChanges();
        result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
        return result;
    };

    const handleCancel = () => {
        setState(structuredClone(originalState));
        setValidationErrors(null);
    };

    const handleSave = async () => {
        const result = await tenantApi.update(state);
        setOriginalState(structuredClone(result));
    };

    const saveDocs = async newDocs => {
        const newTenant = structuredClone(state);
        newTenant.documents = _.filter(newDocs, d => d && !d.shouldDelete);
        const result = await tenantApi.update(newTenant);
        setState(result);
        setOriginalState(structuredClone(result));

        const newResult = { documentHolder: result };

        return newResult;
    };

    useEffect(() => {
        // If the current pathname is for nested tenancy/inventory routes, switch to the Tenancies tab (index 2)
        const p = location.pathname || '';
        const baseDetail = `/tenant/detail/${id}`;
        const baseTop = `/tenant/${id}`;
        if ((p.startsWith(baseDetail) || p.startsWith(baseTop)) && (p.includes('/inventory/') || p.includes('/tenancy/'))) {
            setSelectedIndex(2);
        } else if (tab) {
            const mapping = { 'details': 0, 'profile-images': 1, 'tenancies': 2, 'payments': 3, 'documents': 4 };
            setSelectedIndex(mapping[tab] ?? 0);
        } else {
            setSelectedIndex(0);
        }
    }, [location.pathname, id, tab]);

    // If tenant state hasn't loaded yet, still allow nested tenant routes (inventories/tenancy)
    // to mount by returning an Outlet when the pathname indicates a nested route. Otherwise
    // show a simple loading placeholder.
    if (!state) {
        const p = location.pathname || '';
        const baseDetail = `/tenant/detail/${id}`;
        const baseTop = `/tenant/${id}`;
        if ((p.startsWith(baseDetail) || p.startsWith(baseTop)) && (p.includes('/inventory/') || p.includes('/tenancy/'))) {
            return (
                <div className="tab-content panel p-3 rounded">
                    <Outlet />
                </div>
            );
        }

        return <div className="p-3">Loading tenant...</div>;
    }

    return (
        <TenantContext.Provider value={{
            tenant: state,
            setTenant: setState,
            originalTenant: originalState,
            setOriginalTenant: setOriginalState,
            validationErrors,
            setValidationErrors,
            bankData,
            saveTenant: handleSave,
            saveDocs,
            bankDataError
        }}>
            <Tabs selectedIndex={selectedIndex} onSelect={(i) => setSelectedIndex(i)}>
                <TabList className="nav nav-pills mb-2" >
                    <Tab className="nav-item me-2 mb-2" > <div className="nav-link active">Tenant Details</div></Tab>
                    <Tab className="nav-item me-2 mb-2" > <div className="nav-link active">Tenant Profile Images</div></Tab>
                    <Tab className="nav-item me-2 mb-2" > <div className="nav-link active">Tenancies</div></Tab>
                    <Tab className="nav-item me-2 mb-2" > <div className="nav-link active">Payment History</div></Tab>
                    <Tab className="nav-item me-2 mb-2" > <div className="nav-link active">Documents</div></Tab>
                </TabList>

                <TabPanel>
                    <div className="tab-content panel p-3 rounded">
                        <div className="tab-pane fade active show" id="default-tab-1">
                            <TenantDetailsTab />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel>
                    <div className="tab-content panel p-3 rounded">
                        <div className="tab-pane fade active show" id="default-tab-2">
                            <>Tenant Profile Image UI</>
                        </div>
                    </div>
                </TabPanel>

                <TabPanel>
                    <div className="tab-content panel p-3 rounded">
                        <div className="tab-pane fade active show" id="default-tab-3">
                            {(() => {
                                const p = location.pathname || '';
                                const base = `/tenant/detail/${id}`;
                                const isNested = p.startsWith(base) && (p.includes('/inventory/') || p.includes('/tenancy/'));
                                return !isNested ? <TenanciesTab /> : null;
                            })()}
                        </div>
                        {/* Render nested tenant routes (inventories, inventory detail) inside the Tenancies panel */}
                        <div className="mt-3" id="tenant-nested-route">
                            <Outlet />
                        </div>
                    </div>
                </TabPanel>

                

                <TabPanel>
                    <PaymentsTab />
                </TabPanel>

                <TabPanel>
                    <TenantsDocumentsTab />
                </TabPanel>

            </Tabs>
            <div className="row">
                <div className="col-xl-12">
                    {canSave() && <button className="btn btn-primary" disabled={!canSave()} onClick={() => handleSave()}>Save</button>}
                    {hasChanges() && <button className="btn btn-secondary" disabled={!hasChanges()} onClick={() => handleCancel()}>Cancel</button>}
                    {hasValidationErrors() &&
                        <>
                            {Object.keys(validationErrors).map((e, i) => <>{e}</>)}
                        </>
                    }
                </div>
            </div>
        </TenantContext.Provider>

    );

};